import React, { useState } from "react";
import {
    Table,
    Avatar,
    Tag,
    Button,
    Dropdown,
    Menu,
    Space,
    Tooltip,
    Badge,
    Modal,
    message,
    Switch,
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    MoreOutlined,
    UserOutlined,
    CrownOutlined,
    SafetyOutlined,
    VideoCameraOutlined,
    EyeOutlined,
    StopOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    LockOutlined,
    UnlockOutlined,
    MailOutlined,
    CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./UserTable.scss";

const { confirm } = Modal;

const UserTable = ({
    users,
    loading,
    selectedUsers,
    totalUsers,
    currentPage,
    pageSize,
    onTableChange,
    onUserSelection,
    onEditUser,
    onDeleteUser,
}) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    // Get status config
    const getStatusConfig = (status) => {
        const configs = {
            active: {
                color: "success",
                icon: <CheckCircleOutlined />,
                text: "Active",
            },
            inactive: {
                color: "default",
                icon: <StopOutlined />,
                text: "Inactive",
            },
            banned: {
                color: "error",
                icon: <ExclamationCircleOutlined />,
                text: "Banned",
            },
            pending: {
                color: "warning",
                icon: <ExclamationCircleOutlined />,
                text: "Pending",
            },
        };
        return configs[status] || configs.inactive;
    };

    // Get role config
    const getRoleConfig = (role) => {
        const configs = {
            admin: {
                color: "red",
                icon: <CrownOutlined />,
                text: "Admin",
            },
            moderator: {
                color: "purple",
                icon: <SafetyOutlined />,
                text: "Moderator",
            },
            streamer: {
                color: "blue",
                icon: <VideoCameraOutlined />,
                text: "Streamer",
            },
            user: {
                color: "default",
                icon: <UserOutlined />,
                text: "User",
            },
        };
        return configs[role] || configs.user;
    };

    // Handle user action
    const handleUserAction = (action, user) => {
        switch (action) {
            case "edit":
                onEditUser(user);
                break;
            case "delete":
                showDeleteConfirm(user);
                break;
            case "ban":
                showBanConfirm(user);
                break;
            case "unban":
                handleUnbanUser(user);
                break;
            case "activate":
                handleActivateUser(user);
                break;
            case "deactivate":
                handleDeactivateUser(user);
                break;
            case "view-profile":
                handleViewProfile(user);
                break;
            case "send-email":
                handleSendEmail(user);
                break;
            default:
                break;
        }
    };

    // Show delete confirmation
    const showDeleteConfirm = (user) => {
        confirm({
            title: "Delete User",
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk() {
                onDeleteUser(user);
            },
        });
    };

    // Show ban confirmation
    const showBanConfirm = (user) => {
        confirm({
            title: "Ban User",
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to ban user "${user.username}"?`,
            okText: "Ban",
            okType: "danger",
            cancelText: "Cancel",
            onOk() {
                handleBanUser(user);
            },
        });
    };

    // Handle ban user
    const handleBanUser = (user) => {
        // TODO: Implement ban user API call
        message.success(`User ${user.username} has been banned`);
    };

    // Handle unban user
    const handleUnbanUser = (user) => {
        // TODO: Implement unban user API call
        message.success(`User ${user.username} has been unbanned`);
    };

    // Handle activate user
    const handleActivateUser = (user) => {
        // TODO: Implement activate user API call
        message.success(`User ${user.username} has been activated`);
    };

    // Handle deactivate user
    const handleDeactivateUser = (user) => {
        // TODO: Implement deactivate user API call
        message.success(`User ${user.username} has been deactivated`);
    };

    // Handle view profile
    const handleViewProfile = (user) => {
        // TODO: Implement view profile functionality
        message.info(`Viewing profile for ${user.username}`);
    };

    // Handle send email
    const handleSendEmail = (user) => {
        // TODO: Implement send email functionality
        message.info(`Opening email composer for ${user.email}`);
    };

    // Handle status toggle
    const handleStatusToggle = (user, checked) => {
        const newStatus = checked ? "active" : "inactive";
        // TODO: Implement status toggle API call
        message.success(`User ${user.username} status changed to ${newStatus}`);
    };

    // Get action menu
    const getActionMenu = (user) => {
        const statusConfig = getStatusConfig(user.status);

        return (
            <Menu onClick={({ key }) => handleUserAction(key, user)}>
                {/* <Menu.Item key="edit" icon={<EditOutlined />}>
					Edit User
				</Menu.Item>
				<Menu.Item key="view-profile" icon={<EyeOutlined />}>
					View Profile
				</Menu.Item>
				<Menu.Item key="send-email" icon={<MailOutlined />}>
					Send Email
				</Menu.Item>
				<Menu.Divider />
				{user.status === 'banned' ? (
					<Menu.Item key="unban" icon={<UnlockOutlined />}>
						Unban User
					</Menu.Item>
				) : (
					<Menu.Item key="ban" icon={<LockOutlined />} danger>
						Ban User
					</Menu.Item>
				)}
				{user.status === 'active' ? (
					<Menu.Item key="deactivate" icon={<StopOutlined />}>
						Deactivate
					</Menu.Item>
				) : (
					<Menu.Item key="activate" icon={<CheckCircleOutlined />}>
						Activate
					</Menu.Item>
				)}
				<Menu.Divider /> */}
                <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
                    Delete User
                </Menu.Item>
            </Menu>
        );
    };

    // Expanded row render
    const expandedRowRender = (record) => {
        return (
            <div className="user-details">
                <div className="details-grid">
                    <div className="detail-item">
                        <span className="detail-label">User ID:</span>
                        <span className="detail-value">{record.id}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{record.email}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">
                            {moment(record.createdAt).format(
                                "YYYY-MM-DD HH:mm"
                            )}
                        </span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Last Login:</span>
                        <span className="detail-value">
                            {record.lastLogin
                                ? moment(record.lastLogin).format(
                                      "YYYY-MM-DD HH:mm"
                                  )
                                : "Never"}
                        </span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Streams:</span>
                        <span className="detail-value">
                            {record.streamCount || 0}
                        </span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Followers:</span>
                        <span className="detail-value">
                            {record.followerCount || 0}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // Table columns
    const columns = [
        {
            title: "User",
            dataIndex: "username",
            key: "username",
            width: 250,
            fixed: "left",
            // sorter: true,
            render: (text, record) => (
                <div className="user-cell">
                    <Avatar
                        src={record.image360}
                        icon={<UserOutlined />}
                        size={40}
                        className="user-avatar"
                    />
                    <div className="user-info">
                        <div className="user-name">
                            {record.name || record.producerId}
                            {record.verified && (
                                <Tooltip title="Verified User">
                                    <CheckCircleOutlined className="verified-icon" />
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Email",
            dataIndex: "producerId",
            key: "producerId",
            width: 200,
            // sorter: true,
            render: (email) => (
                <Tooltip title={email}>
                    <span className="user-email">{email}</span>
                </Tooltip>
            ),
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            width: 120,
            // sorter: true,
            // filters: [
            // 	{ text: 'Admin', value: 'admin' },
            // 	{ text: 'Moderator', value: 'moderator' },
            // 	{ text: 'Streamer', value: 'streamer' },
            // 	{ text: 'User', value: 'user' }
            // ],
            render: (role) => {
                const config = getRoleConfig(role);
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            },
        },
        // {
        // 	title: 'Status',
        // 	dataIndex: 'status',
        // 	key: 'status',
        // 	width: 120,
        // 	sorter: true,
        // 	filters: [
        // 		{ text: 'Active', value: 'active' },
        // 		{ text: 'Inactive', value: 'inactive' },
        // 		{ text: 'Banned', value: 'banned' },
        // 		{ text: 'Pending', value: 'pending' }
        // 	],
        // 	render: (status, record) => {
        // 		const config = getStatusConfig(status);
        // 		return (
        // 			<div className="status-cell">
        // 				<Badge
        // 					status={config.color === 'success' ? 'success' :
        // 						   config.color === 'error' ? 'error' :
        // 						   config.color === 'warning' ? 'warning' : 'default'}
        // 					text={config.text}
        // 				/>
        // 				{status !== 'banned' && (
        // 					<Switch
        // 						size="small"
        // 						checked={status === 'active'}
        // 						onChange={(checked) => handleStatusToggle(record, checked)}
        // 						className="status-toggle"
        // 					/>
        // 				)}
        // 			</div>
        // 		);
        // 	}
        // },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 150,
            // sorter: true,
            render: (date) => (
                <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
                    <span className="date-cell">
                        <CalendarOutlined className="date-icon" />
                        {moment(date).format("MMM DD, YYYY")}
                    </span>
                </Tooltip>
            ),
        },
        // {
        // 	title: 'Last Login',
        // 	dataIndex: 'lastLogin',
        // 	key: 'lastLogin',
        // 	width: 150,
        // 	sorter: true,
        // 	render: (date) => (
        // 		<Tooltip title={date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : 'Never logged in'}>
        // 			<span className="date-cell">
        // 				{date ? (
        // 					<>
        // 						<CalendarOutlined className="date-icon" />
        // 						{moment(date).fromNow()}
        // 					</>
        // 				) : (
        // 					<span className="never-login">Never</span>
        // 				)}
        // 			</span>
        // 		</Tooltip>
        // 	)
        // },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            fixed: "right",
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit User">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleUserAction("edit", record)}
                            className="action-btn edit-btn"
                        />
                    </Tooltip>
                    <Dropdown
                        overlay={getActionMenu(record)}
                        trigger={["click"]}
                        placement="bottomRight"
                    >
                        <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            className="action-btn more-btn"
                        />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    // Row selection config
    const rowSelection = {
        selectedRowKeys: selectedUsers.map((user) => user.id),
        onChange: (selectedRowKeys, selectedRows) => {
            onUserSelection(selectedRowKeys, selectedRows);
        },
        getCheckboxProps: (record) => ({
            disabled: record.role === "admin", // Disable admin selection
        }),
    };

    // Handle expand
    const handleExpand = (expanded, record) => {
        const keys = expanded
            ? [...expandedRowKeys, record.id]
            : expandedRowKeys.filter((key) => key !== record.id);
        setExpandedRowKeys(keys);
    };
    return (
        <div className="user-table-wrapper">
            <Table
                columns={columns}
                dataSource={users}
                rowKey="id"
                loading={loading}
                // rowSelection={rowSelection}
                // expandable={{
                // 	expandedRowRender,
                // 	expandedRowKeys,
                // 	onExpand: handleExpand,
                // 	expandIcon: ({ expanded, onExpand, record }) => (
                // 		<Button
                // 			type="text"
                // 			size="small"
                // 			icon={expanded ? <EyeOutlined /> : <EyeOutlined />}
                // 			onClick={e => onExpand(record, e)}
                // 			className="expand-btn"
                // 		/>
                // 	)
                // }}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalUsers,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} users`,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    className: "table-pagination",
                }}
                onChange={onTableChange}
                size="middle"
                className="user-table"
            />
        </div>
    );
};

export default UserTable;
